import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    IconButton,
    Modal,
    Paper,
    Box,
    Typography,
    Fade,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    LinearProgress,
    Popover,
    Chip,
} from '@mui/material';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import CloseIcon from '@mui/icons-material/Close';
import DateRangeIcon from '@mui/icons-material/DateRange';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
    getTripPerformance,
    exportTripPerformance,
    tripPerformanceSelector,
    tripPerformanceLoadingSelector,
    tripPerformanceCacheKeySelector
} from '../../../features/tripcenter/tripControlSlice';
import { distancePeriodsMain, getperiod } from '../../../utils/datePeriodCalculator';

const DEFAULT_PERIOD = 'thismonthtonow';
const TripPerformanceModal = () => {
    const dispatch = useDispatch();
    const [open, setOpen] = useState(false);
    const [selectedPeriod, setSelectedPeriod] = useState(DEFAULT_PERIOD);
    const [previousPeriod, setPreviousPeriod] = useState(DEFAULT_PERIOD);
    const [customDialogOpen, setCustomDialogOpen] = useState(false);
    const [customFromDate, setCustomFromDate] = useState(null);
    const [customToDate, setCustomToDate] = useState(null);
    const [customDateError, setCustomDateError] = useState('');
    const [customRangeApplied, setCustomRangeApplied] = useState(false);
    const [appliedCustomFromDate, setAppliedCustomFromDate] = useState(null);
    const [appliedCustomToDate, setAppliedCustomToDate] = useState(null);
    const [selectedType, setSelectedType] = useState('route');
    const [tripStage, setTripStage] = useState('all');
    const [listPopover, setListPopover] = useState({
        anchorEl: null,
        title: '',
        items: []
    });

    const performance = useSelector(tripPerformanceSelector) || {};
    const loading = useSelector(tripPerformanceLoadingSelector);
    const cacheKey = useSelector(tripPerformanceCacheKeySelector);

    const periodOptions = [...distancePeriodsMain, { name: 'Custom Range', value: 'custom' }];

    const stageOptions = [
        { label: 'All', value: 'all' },
        { label: 'Pending', value: 'pending' },
        { label: 'Active', value: 'Active' },
        { label: 'Completed', value: 'Completed' },
    ];

    const getDateRange = (periodOverride) => {
        const effectivePeriod = periodOverride || selectedPeriod;
        if (effectivePeriod === 'custom') {
            if (appliedCustomFromDate && appliedCustomToDate) {
                return {
                    startDate: appliedCustomFromDate.format('YYYY-MM-DD'),
                    endDate: appliedCustomToDate.format('YYYY-MM-DD')
                };
            }
            return {};
        }
        const { fromDate, toDate } = getperiod(effectivePeriod) || {};
        return {
            startDate: fromDate ? fromDate.format('YYYY-MM-DD') : undefined,
            endDate: toDate ? toDate.format('YYYY-MM-DD') : undefined
        };
    };

    const requestPerformance = ({
        periodOverride,
        rangeOverride,
        typeOverride,
        stageOverride
    } = {}) => {
        const { startDate, endDate } = rangeOverride || getDateRange(periodOverride);
        const resolvedType = typeOverride || selectedType;
        const resolvedStage = stageOverride !== undefined ? stageOverride : tripStage;
        const stageValue = resolvedStage === 'all' ? undefined : resolvedStage;
        dispatch(getTripPerformance({
            type: resolvedType,
            startDate,
            endDate,
            tripStage: stageValue
        }));
    };

    const openCustomDialog = () => {
        const basePeriod = selectedPeriod === 'custom' ? previousPeriod : selectedPeriod;
        if (appliedCustomFromDate && appliedCustomToDate) {
            setCustomFromDate(appliedCustomFromDate);
            setCustomToDate(appliedCustomToDate);
        } else if (!customFromDate || !customToDate) {
            const { fromDate, toDate } = getperiod(basePeriod) || {};
            setCustomFromDate(fromDate ? dayjs(fromDate.toDate()) : dayjs());
            setCustomToDate(toDate ? dayjs(toDate.toDate()) : dayjs());
        }
        setCustomDateError('');
        setCustomDialogOpen(true);
    };

    const handleCustomDialogClose = () => {
        setCustomDialogOpen(false);
        setCustomDateError('');
        if (!customRangeApplied) {
            setSelectedPeriod(previousPeriod);
            setCustomFromDate(null);
            setCustomToDate(null);
            return;
        }
        if (appliedCustomFromDate && appliedCustomToDate) {
            setCustomFromDate(appliedCustomFromDate);
            setCustomToDate(appliedCustomToDate);
        }
    };

    const handleCustomRangeSubmit = () => {
        if (!customFromDate || !customToDate) {
            setCustomDateError('Please select both dates.');
            return;
        }

        if (customFromDate.isAfter(customToDate)) {
            setCustomDateError('From date cannot be after To date.');
            return;
        }

        setCustomDateError('');
        setSelectedPeriod('custom');
        setCustomRangeApplied(true);
        setAppliedCustomFromDate(customFromDate);
        setAppliedCustomToDate(customToDate);
        setCustomDialogOpen(false);
        requestPerformance({
            rangeOverride: {
                startDate: customFromDate.format('YYYY-MM-DD'),
                endDate: customToDate.format('YYYY-MM-DD')
            }
        });
    };

    const handlePeriodChange = (event) => {
        const newPeriod = event.target.value;
        setPreviousPeriod(selectedPeriod);
        if (newPeriod === 'custom') {
            setSelectedPeriod('custom');
            openCustomDialog();
            return;
        }
        setCustomRangeApplied(false);
        setSelectedPeriod(newPeriod);
        const { fromDate, toDate } = getperiod(newPeriod) || {};
        requestPerformance({
            rangeOverride: {
                startDate: fromDate ? fromDate.format('YYYY-MM-DD') : undefined,
                endDate: toDate ? toDate.format('YYYY-MM-DD') : undefined
            }
        });
    };

    const handleOpen = () => {
        setOpen(true);

        if (selectedPeriod === 'custom') {
            if (appliedCustomFromDate && appliedCustomToDate) {
                requestPerformance();
            } else {
                openCustomDialog();
            }
            return;
        }

        requestPerformance();
    };

    const handleClose = () => {
        setOpen(false);
        handleListPopoverClose();
    };

    useEffect(() => {
        if (!open && listPopover.anchorEl) {
            handleListPopoverClose();
        }
    }, [open, listPopover.anchorEl]);

    const handleTypeChange = (event) => {
        const nextType = event.target.value;
        setSelectedType(nextType);
        requestPerformance({ typeOverride: nextType });
    };

    const handleTripStageChange = (event) => {
        const nextStage = event.target.value;
        setTripStage(nextStage);
        requestPerformance({ stageOverride: nextStage });
    };

    const handleExport = (exportType) => {
        const { startDate, endDate } = getDateRange();
        const stageValue = tripStage === 'all' ? undefined : tripStage;
        dispatch(exportTripPerformance({
            type: selectedType,
            exportType,
            cacheKey,
            startDate,
            endDate,
            tripStage: stageValue
        }));
    };

    const normalizeList = (value) => {
        if (Array.isArray(value)) return value.filter(Boolean);
        if (value) return [value];
        return [];
    };

    const handleListPopoverOpen = (event, title, items) => {
        setListPopover({
            anchorEl: event.currentTarget,
            title,
            items
        });
    };

    const handleListPopoverClose = () => {
        setListPopover({
            anchorEl: null,
            title: '',
            items: []
        });
    };

    const renderAssociatedList = (value, title) => {
        const items = normalizeList(value);
        if (items.length === 0) {
            return (
                <Typography variant="caption" color="text.disabled">
                    -
                </Typography>
            );
        }

        const visibleCount = items.length > 3 ? 2 : items.length;
        const visibleItems = items.slice(0, visibleCount);
        const remaining = items.length - visibleCount;
        const inlineText = visibleItems.join(', ');

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, color: 'text.primary', whiteSpace: 'normal' }}
                >
                    {inlineText}
                </Typography>
                {remaining > 0 && (
                    <Button
                        variant="text"
                        size="small"
                        onClick={(event) => handleListPopoverOpen(event, title, items)}
                        sx={{
                            textTransform: 'none',
                            minWidth: 0,
                            px: 0.5,
                            fontSize: '0.7rem',
                            lineHeight: 1,
                            fontWeight: 700
                        }}
                    >
                        & {remaining} more
                    </Button>
                )}
            </Box>
        );
    };

    const getCurrentPeriodName = () => {
        if (selectedPeriod === 'custom') {
            return 'Custom Range';
        }
        const period = distancePeriodsMain.find(p => p.value === selectedPeriod);
        return period ? period.name : 'Custom Period';
    };

    const getDateRangeString = () => {
        if (selectedPeriod === 'custom') {
            if (appliedCustomFromDate && appliedCustomToDate) {
                return `${appliedCustomFromDate.format('MMM D, YYYY')} - ${appliedCustomToDate.format('MMM D, YYYY')}`;
            }
            return 'Select custom dates';
        }
        const { fromDate, toDate } = getperiod(selectedPeriod) || {};
        if (!fromDate || !toDate) {
            return 'Not available';
        }
        return `${fromDate.format('MMM D, YYYY')} - ${toDate.format('MMM D, YYYY')}`;
    };

    const list = performance.list || [];
    const totalCount = performance.totalCount || 0;

    const formatNumber = (value) => Math.round(value || 0).toLocaleString('en-IN');

    const stageConfig = {
        Completed: { key: 'completedTrips', label: 'Completed', short: 'C', color: 'success.main' },
        Active: { key: 'activeTrips', label: 'Active', short: 'A', color: 'warning.main' },
        pending: { key: 'pendingTrips', label: 'Pending', short: 'P', color: 'error.main' }
    };

    const renderTripBreakdown = (row) => {
        if (tripStage !== 'all') {
            const config = stageConfig[tripStage];
            const value = row?.[config?.key] || 0;
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: config?.color }} />
                    <Typography variant="caption" sx={{ fontWeight: 600, color: config?.color }}>
                        {config?.label}: {value}
                    </Typography>
                </Box>
            );
        }

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                {['Completed', 'Active', 'pending'].map((key) => {
                    const config = stageConfig[key];
                    return (
                        <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: config.color }} />
                            <Typography variant="caption" sx={{ fontWeight: 600, color: config.color }}>
                                {config.short} {row?.[config.key] || 0}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        );
    };

    const getColumns = () => {
        if (selectedType === 'route') {
            return [
                {
                    key: 'route',
                    label: 'Route',
                    width: '3.4fr',
                    align: 'left',
                    render: (row) => (
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                            #{row.routeNumber} - {row.routeName} | {row.routeType}
                        </Typography>
                    )
                },
                {
                    key: 'routeLength',
                    label: 'Route Km',
                    width: '0.9fr',
                    align: 'right',
                    render: (row) => (
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                            {formatNumber(row.routeLength)}
                        </Typography>
                    )
                },
                {
                    key: 'totalTrips',
                    label: 'Trips',
                    width: '0.9fr',
                    align: 'right',
                    render: (row) => (
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.8125rem' }}>
                            {formatNumber(row.totalTrips)}
                        </Typography>
                    )
                },
                {
                    key: 'totalKilometers',
                    label: 'Km Run',
                    width: '1fr',
                    align: 'right',
                    render: (row) => (
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'info.main', fontSize: '0.8125rem' }}>
                            {formatNumber(row.totalKilometers)}
                        </Typography>
                    )
                },
                {
                    key: 'breakdown',
                    label: 'Status',
                    width: '1.6fr',
                    align: 'center',
                    render: (row) => renderTripBreakdown(row)
                }
            ];
        }

        if (selectedType === 'truck' || selectedType === 'driver') {
            return [
                {
                    key: 'name',
                    label: selectedType === 'truck' ? 'Truck' : 'Driver',
                    width: '2.1fr',
                    align: 'left',
                    render: (row) => (
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                            {selectedType === 'truck' ? row.number : row.name}
                        </Typography>
                    )
                },
                {
                    key: 'associations',
                    label: selectedType === 'truck' ? 'Drivers' : 'Trucks',
                    width: '1.7fr',
                    align: 'left',
                    render: (row) => (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                            {selectedType === 'truck'
                                ? renderAssociatedList(row.drivers, 'Drivers')
                                : renderAssociatedList(row.trucks, 'Trucks')}
                        </Box>
                    )
                },
                {
                    key: 'tripCount',
                    label: 'Trips',
                    width: '0.9fr',
                    align: 'right',
                    render: (row) => (
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.8125rem' }}>
                            {formatNumber(row.tripCount)}
                        </Typography>
                    )
                },
                {
                    key: 'totalKilometers',
                    label: 'Km Run',
                    width: '1fr',
                    align: 'right',
                    render: (row) => (
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'info.main', fontSize: '0.8125rem' }}>
                            {formatNumber(row.totalKilometers)}
                        </Typography>
                    )
                },
                {
                    key: 'breakdown',
                    label: 'Status',
                    width: '1.6fr',
                    align: 'center',
                    render: (row) => renderTripBreakdown(row)
                }
            ];
        }

        return [
            {
                key: 'name',
                label: 'Destination',
                width: '2.2fr',
                align: 'left',
                render: (row) => (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                            {row.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {row.locationType || 'Unknown'}
                        </Typography>
                    </Box>
                )
            },
            {
                key: 'visitCount',
                label: 'Visits',
                width: '0.7fr',
                align: 'right',
                render: (row) => (
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                        {formatNumber(row.visitCount)}
                    </Typography>
                )
            },
            {
                key: 'tripCount',
                label: 'Trips',
                width: '0.7fr',
                align: 'right',
                render: (row) => (
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                        {formatNumber(row.tripCount)}
                    </Typography>
                )
            },
            {
                key: 'avgDwellTime',
                label: 'Avg Dwell',
                width: '0.9fr',
                align: 'right',
                render: (row) => (
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                        {formatNumber(row.avgDwellTime)}
                    </Typography>
                )
            },
            {
                key: 'totalDwellTime',
                label: 'Total Dwell',
                width: '0.9fr',
                align: 'right',
                render: (row) => (
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                        {formatNumber(row.totalDwellTime)}
                    </Typography>
                )
            },
            {
                key: 'totalKilometers',
                label: 'Km Run',
                width: '0.9fr',
                align: 'right',
                render: (row) => (
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'info.main', fontSize: '0.8125rem' }}>
                        {formatNumber(row.totalKilometers)}
                    </Typography>
                )
            }
        ];
    };

    const columns = getColumns();
    const gridTemplateColumns = columns.map(col => col.width).join(' ');
    const justifyForAlign = (align) => {
        if (align === 'center') return 'center';
        if (align === 'right') return 'flex-end';
        return 'flex-start';
    };

    return (
        <>
            <IconButton
                onClick={handleOpen}
                sx={{
                    bgcolor: open ? 'primary.main' : 'transparent',
                    color: open ? 'white' : 'primary.main',
                    padding: 1,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        bgcolor: open ? 'primary.dark' : 'action.hover'
                    }
                }}
            >
                <QueryStatsIcon fontSize="small" />
            </IconButton>

            <Dialog
                open={customDialogOpen}
                onClose={handleCustomDialogClose}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle sx={{ fontWeight: 700, textAlign: 'center' }}>Select Custom Dates</DialogTitle>
                <DialogContent dividers>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Box sx={{ display: 'grid', gap: 2 }}>
                            <DatePicker
                                label="From date"
                                value={customFromDate}
                                onChange={(newValue) => setCustomFromDate(newValue)}
                                format="DD MMM YYYY"
                                slotProps={{
                                    textField: { fullWidth: true, size: 'small' }
                                }}
                            />
                            <DatePicker
                                label="To date"
                                value={customToDate}
                                onChange={(newValue) => setCustomToDate(newValue)}
                                format="DD MMM YYYY"
                                slotProps={{
                                    textField: { fullWidth: true, size: 'small' }
                                }}
                            />
                        </Box>
                    </LocalizationProvider>
                    {customDateError && (
                        <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                            {customDateError}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={handleCustomDialogClose} color="inherit">Cancel</Button>
                    <Button variant="contained" onClick={handleCustomRangeSubmit}>Apply</Button>
                </DialogActions>
            </Dialog>

            <Modal
                open={open}
                onClose={handleClose}
                closeAfterTransition
            >
                <Fade in={open}>
                    <Paper
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '96%',
                            maxWidth: 1280,
                            maxHeight: '90vh',
                            overflow: 'auto',
                            bgcolor: 'background.paper',
                            borderRadius: 2,
                            boxShadow: 24,
                            p: 0
                        }}
                    >
                        <Box
                            sx={{
                                p: 2,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 2
                            }}
                        >
                            <Typography variant="h6" fontWeight="bold">
                                Trip Performance
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <FormControl
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        minWidth: 170,
                                        '& .MuiOutlinedInput-root': {
                                            color: 'white',
                                            '& fieldset': {
                                                borderColor: 'rgba(255, 255, 255, 0.5)',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: 'white',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: 'white',
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                        },
                                        '& .MuiSvgIcon-root': {
                                            color: 'white',
                                        }
                                    }}
                                >
                                    <InputLabel id="trip-perf-period-label">Period</InputLabel>
                                    <Select
                                        labelId="trip-perf-period-label"
                                        id="trip-perf-period"
                                        value={selectedPeriod}
                                        onChange={handlePeriodChange}
                                        label="Period"
                                        startAdornment={<DateRangeIcon sx={{ mr: 1 }} />}
                                    >
                                        {periodOptions.map((period) => (
                                            <MenuItem
                                                key={period.value}
                                                value={period.value}
                                                onClick={period.value === 'custom' ? () => {
                                                    if (selectedPeriod === 'custom') {
                                                        openCustomDialog();
                                                    }
                                                } : undefined}
                                            >
                                                {period.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        minWidth: 150,
                                        '& .MuiOutlinedInput-root': {
                                            color: 'white',
                                            '& fieldset': {
                                                borderColor: 'rgba(255, 255, 255, 0.5)',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: 'white',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: 'white',
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                        },
                                        '& .MuiSvgIcon-root': {
                                            color: 'white',
                                        }
                                    }}
                                >
                                    <InputLabel id="trip-perf-stage-label">Trip Stage</InputLabel>
                                    <Select
                                        labelId="trip-perf-stage-label"
                                        id="trip-perf-stage"
                                        value={tripStage}
                                        onChange={handleTripStageChange}
                                        label="Trip Stage"
                                    >
                                        {stageOptions.map((stage) => (
                                            <MenuItem key={stage.value} value={stage.value}>
                                                {stage.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <IconButton size="small" onClick={handleClose} sx={{ color: 'white' }}>
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                bgcolor: 'primary.50',
                                p: 1.25,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            <DateRangeIcon color="primary" fontSize="small" />
                            <Typography variant="body2" color="primary.dark">
                                Showing data for: <strong>{getCurrentPeriodName()}</strong> ({getDateRangeString()})
                            </Typography>
                        </Box>

                        {loading && list.length > 0 && <LinearProgress />}

                        <Box sx={{ p: 2 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 2,
                                    flexWrap: 'wrap',
                                    mb: 2
                                }}
                            >
                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                    <InputLabel id="trip-perf-type-label">Type</InputLabel>
                                    <Select
                                        labelId="trip-perf-type-label"
                                        id="trip-perf-type"
                                        value={selectedType}
                                        onChange={handleTypeChange}
                                        label="Type"
                                    >
                                        <MenuItem value="route">Route</MenuItem>
                                        <MenuItem value="truck">Truck</MenuItem>
                                        <MenuItem value="driver">Driver</MenuItem>
                                        <MenuItem value="destination">Destination</MenuItem>
                                    </Select>
                                </FormControl>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handleExport('excel')}
                                        disabled={loading}
                                    >
                                        Export Excel
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handleExport('pdf')}
                                        disabled={loading}
                                    >
                                        Export PDF
                                    </Button>
                                </Box>
                            </Box>

                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                Showing {formatNumber(totalCount)} records
                            </Typography>

                            <Paper
                                elevation={0}
                                variant="outlined"
                                sx={{
                                    p: 0,
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns,
                                        borderBottom: 1,
                                        borderColor: 'divider',
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        pr: 1,
                                    }}
                                >
                                    {columns.map((col) => (
                                        <Box
                                            key={col.key}
                                            sx={{
                                                p: 1,
                                                fontWeight: 700,
                                                fontSize: '0.72rem',
                                                color: 'white',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.04em',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: justifyForAlign(col.align),
                                                textAlign: col.align || 'left',
                                                borderRight: 1,
                                                borderColor: 'rgba(255,255,255,0.15)'
                                            }}
                                        >
                                            {col.label}
                                        </Box>
                                    ))}
                                </Box>

                                <Box sx={{ maxHeight: '55vh', overflow: 'auto' }}>
                                    {loading && list.length === 0 ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                                            <CircularProgress size={28} />
                                        </Box>
                                    ) : list.length > 0 ? (
                                        list.map((row, index) => (
                                            <Box
                                                key={index}
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns,
                                                    borderBottom: index < list.length - 1 ? 1 : 0,
                                                    borderColor: 'divider',
                                                    '&:hover': { bgcolor: 'action.hover' }
                                                }}
                                            >
                                                {columns.map((col) => (
                                                    <Box
                                                        key={col.key}
                                                        sx={{
                                                            p: 1,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: justifyForAlign(col.align),
                                                            textAlign: col.align || 'left',
                                                            borderRight: 1,
                                                            borderColor: 'divider',
                                                            minHeight: 36
                                                        }}
                                                    >
                                                        {col.render(row)}
                                                    </Box>
                                                ))}
                                            </Box>
                                        ))
                                    ) : (
                                        <Box sx={{ p: 3, textAlign: 'center' }}>
                                            <Typography variant="body2" color="text.disabled">No data available</Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Paper>

                        </Box>
                    </Paper>
                </Fade>
            </Modal>

            <Popover
                open={Boolean(listPopover.anchorEl)}
                anchorEl={listPopover.anchorEl}
                onClose={handleListPopoverClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                    sx: {
                        p: 1.5,
                        maxWidth: 320,
                        maxHeight: 240,
                        overflow: 'auto'
                    }
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    {listPopover.title}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {listPopover.items.map((item, index) => (
                        <Chip
                            key={`${item}-${index}`}
                            label={item}
                            size="small"
                            sx={{ fontWeight: 600 }}
                        />
                    ))}
                </Box>
            </Popover>
        </>
    );
};

export default TripPerformanceModal;
